import React, { PropsWithChildren, createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useOrganization } from "./GraphqlProvider";
import { useAuth } from "~/state/auth";
import { registerWsConnection, unregisterWsConnection } from "~/state/auth/wsConnectionRegistry";
import {
  createWebsocketClient,
  MinimalSocket,
  ReadyState,
  WebsocketClient,
  WebsocketMessage,
  WebsocketSocket,
} from "@caredna-marlin/utils/client/websocketClient";

// Ping the server this often while the socket is open
const PING_INTERVAL_MS = 25_000;

const MarlinSocketContext = createContext<WebsocketSocket | null>(null);

export const useSharedMarlinSocket = () => useContext(MarlinSocketContext);

// Verbose WS logging is opt-in: set `debugMode = true` in the browser console to enable, and
// unset it (or reload) to silence.
const isDebugEnabled = (): boolean => !!(window as { debugMode?: boolean }).debugMode;

export const WebSocketProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const wsUrl = import.meta.env.VITE_WS_URL;
  const { organizationId } = useOrganization();
  const { isAuthenticated } = useAuth();
  const shouldConnect = !!isAuthenticated && !!organizationId && !!wsUrl;

  const [readyState, setReadyState] = useState<ReadyState>(ReadyState.UNINSTANTIATED);
  const [lastJsonMessage, setLastJsonMessage] = useState<WebsocketMessage | null>(null);
  const clientRef = useRef<WebsocketClient | null>(null);

  // The connectionId this tab last registered with the auth layer's shared registry (or
  // undefined if none), so it can be unregistered on a fresh connect or on teardown.
  const registeredConnectionIdRef = useRef<string | undefined>(undefined);

  const handleConnectionId = useCallback((connectionId: string | undefined) => {
    if (connectionId) {
      registeredConnectionIdRef.current = connectionId;
      registerWsConnection(connectionId);
    } else if (registeredConnectionIdRef.current) {
      unregisterWsConnection(registeredConnectionIdRef.current);
      registeredConnectionIdRef.current = undefined;
    }
  }, []);

  const sendJsonMessage = useCallback((message: unknown) => {
    return clientRef.current?.sendJsonMessage(message) ?? false;
  }, []);

  useEffect(() => {
    // wsUrl and organizationId are here to keep typescript happy even though shouldConnect
    // already includes them.
    if (!shouldConnect || !wsUrl || !organizationId) {
      setReadyState(ReadyState.UNINSTANTIATED);
      return;
    }

    const client = createWebsocketClient({
      wsUrl,
      organizationId,
      // The browser attaches the auth cookie to the handshake automatically (unlike mobile,
      // there's no header to set here), and a native WebSocket already satisfies MinimalSocket
      // at runtime — the cast just bridges TypeScript's richer DOM event types down to the
      // client's simpler ones.
      connect: (url) => new WebSocket(url) as unknown as MinimalSocket,
      pingIntervalMs: PING_INTERVAL_MS,
      onReadyStateChange: setReadyState,
      onMessage: setLastJsonMessage,
      onConnectionId: handleConnectionId,
      isDebugEnabled,
    });
    clientRef.current = client;
    client.start();

    return () => {
      client.stop();
      clientRef.current = null;
    };
  }, [shouldConnect, wsUrl, organizationId, handleConnectionId]);

  return (
    <MarlinSocketContext.Provider value={{ readyState, lastJsonMessage, sendJsonMessage }}>
      {children}
    </MarlinSocketContext.Provider>
  );
};
